/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { TenantConfiguration } from '../models/tenant-configuration';


/**
 * API for tenant configuration.
 */
@Injectable({
  providedIn: 'root',
})
export class ConfigService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation refreshTenantConfig
   */
  static readonly RefreshTenantConfigPath = '/config/refresh';

  /**
   * Refresh tenant configurations.
   *
   * Forces an immediate refresh of all tenant configurations from Azure App Configuration. Use this after modifying configuration values to apply changes without waiting for the scheduled cache refresh.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `refreshTenantConfig()` instead.
   *
   * This method doesn't expect any request body.
   */
  refreshTenantConfig$Response(params?: {
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ConfigService.RefreshTenantConfigPath, 'post');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Refresh tenant configurations.
   *
   * Forces an immediate refresh of all tenant configurations from Azure App Configuration. Use this after modifying configuration values to apply changes without waiting for the scheduled cache refresh.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `refreshTenantConfig$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  refreshTenantConfig(params?: {
  },
  context?: HttpContext

): Observable<void> {

    return this.refreshTenantConfig$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation setMaintenanceMode
   */
  static readonly SetMaintenanceModePath = '/config/maintenance-mode/{enabled}';

  /**
   * Enable or disable maintenance mode for current tenant.
   *
   * Sets maintenance mode status. When enabled, the application will be in maintenance mode for this tenant. Configuration changes are applied immediately via automatic cache refresh.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `setMaintenanceMode()` instead.
   *
   * This method doesn't expect any request body.
   */
  setMaintenanceMode$Response(params: {

    /**
     * Enable (true) or disable (false) maintenance mode
     */
    enabled: boolean;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, ConfigService.SetMaintenanceModePath, 'post');
    if (params) {
      rb.path('enabled', params.enabled, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Enable or disable maintenance mode for current tenant.
   *
   * Sets maintenance mode status. When enabled, the application will be in maintenance mode for this tenant. Configuration changes are applied immediately via automatic cache refresh.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `setMaintenanceMode$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  setMaintenanceMode(params: {

    /**
     * Enable (true) or disable (false) maintenance mode
     */
    enabled: boolean;
  },
  context?: HttpContext

): Observable<void> {

    return this.setMaintenanceMode$Response(params,context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getTenantConfig
   */
  static readonly GetTenantConfigPath = '/config/public/{tenantId}';

  /**
   * Get configuration for a tenant.
   *
   * Retrieves the single sign-on configuration for the given tenant identifier. Throws 404 if the tenant is not found.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getTenantConfig()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantConfig$Response(params: {

    /**
     * The unique identifier of the tenant
     */
    tenantId: string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<TenantConfiguration>> {

    const rb = new RequestBuilder(this.rootUrl, ConfigService.GetTenantConfigPath, 'get');
    if (params) {
      rb.path('tenantId', params.tenantId, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<TenantConfiguration>;
      })
    );
  }

  /**
   * Get configuration for a tenant.
   *
   * Retrieves the single sign-on configuration for the given tenant identifier. Throws 404 if the tenant is not found.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getTenantConfig$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getTenantConfig(params: {

    /**
     * The unique identifier of the tenant
     */
    tenantId: string;
  },
  context?: HttpContext

): Observable<TenantConfiguration> {

    return this.getTenantConfig$Response(params,context).pipe(
      map((r: StrictHttpResponse<TenantConfiguration>) => r.body as TenantConfiguration)
    );
  }

}
